'use client';

import { useState } from 'react';

import Link from 'next/link';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { Heading } from '@eliadmin/core/src/components/ui/heading';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoArrowUpRight } from 'react-icons/go';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';

const gridClassName = cn(
  'grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-6 lg:grid-cols-4 xl:grid-cols-5',
);

function SubcategoryCard({
  subcategory,
}: {
  subcategory: NonNullable<Category['sub_categories']>[number];
}) {
  return (
    <Link
      href={subcategory?.slug ?? ''}
      className={cn(
        'ea-rounded-xl flex items-start justify-between gap-3 bg-white p-3 md:p-6',
        'ea-subcategory-card',
      )}
    >
      <Heading title={subcategory.name} tag="h2" visualTag="h5" />
      <GoArrowUpRight className="max-h-5 min-h-5 min-w-5 max-w-5 text-border md:max-h-6 md:min-h-6 md:min-w-6 md:max-w-6" />
    </Link>
  );
}

export function CategorySubcategories({
  subcategories,
}: {
  subcategories: Category['sub_categories'];
}) {
  const isDesktop = useBreakpoint('sm');
  const VISIBLE_LIMIT = isDesktop ? 10 : 8;

  const { t } = useTranslations();
  const [isExpanded, setIsExpanded] = useState(false);

  const visibleSubcategories = subcategories?.slice(0, VISIBLE_LIMIT) ?? [];
  const hiddenSubcategories = subcategories?.slice(VISIBLE_LIMIT) ?? [];
  const hasHiddenSubcategories = hiddenSubcategories.length > 0;

  return (
    <section
      className={cn('custom-section', 'gap-6 pb-6 pt-10 sm:pb-8 sm:pt-10 lg:pb-12 lg:pt-11')}
    >
      <div className={cn('custom-container', 'flex flex-col gap-3 md:gap-6')}>
        <div className={gridClassName}>
          {visibleSubcategories.map(subcategory => (
            <SubcategoryCard key={subcategory?.slug} subcategory={subcategory} />
          ))}
        </div>

        {hasHiddenSubcategories && (
          <div
            className={cn(
              'grid transition-[grid-template-rows] duration-300 ease-in-out',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="overflow-hidden">
              <div className={cn(gridClassName, 'pt-3 md:pt-6')}>
                {hiddenSubcategories.map(subcategory => (
                  <SubcategoryCard key={subcategory?.slug} subcategory={subcategory} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {hasHiddenSubcategories && (
        <span
          role="button"
          tabIndex={0}
          onClick={() => setIsExpanded(prev => !prev)}
          onKeyDown={event => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsExpanded(prev => !prev);
            }
          }}
          className="cursor-pointer text-center text-sm font-medium text-primary transition-all hover:opacity-70 sm:text-base"
        >
          {isExpanded ? t('text604') : t('text338')}
        </span>
      )}
    </section>
  );
}
